import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../components/Footer';
import { getServiceArea, serviceAreas } from '../data/areas';
import { ArrowRight, MapPin, Phone } from 'lucide-react';

const services = [
  { name: 'Kitchen Renovation', path: '/services/kitchen-renovation-westchester' },
  { name: 'Bathroom Remodeling', path: '/services/bathroom-remodeling-westchester' },
  { name: 'Home Additions', path: '/services/home-additions-westchester' },
  { name: 'Interior Renovations', path: '/services/interior-renovations' },
  { name: 'Basement Finishing', path: '/services/basement-finishing' },
  { name: 'Exterior Improvements', path: '/services/exterior-improvements' },
];

export function AreaPage() {
  const { slug } = useParams();
  const area = getServiceArea(slug);

  if (!area) {
    return (
      <div className="min-h-screen bg-zinc-900 text-zinc-100 pt-32 px-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-light mb-6">Area not found</h1>
          <Link to="/" className="text-yellow-400">Back to home</Link>
        </div>
      </div>
    );
  }

  const place = `${area.name}, ${area.state}`;

  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>{`Home Renovation in ${place} | Golden Touch`}</title>
        <meta name="description" content={area.description} />
        <link rel="canonical" href={`https://renovationwestchester.com/areas/${area.slug}`} />
      </Helmet>

      <main className="pt-24">
        <section className="relative min-h-[52vh] flex items-end">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                'url("https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80")',
            }}
            role="img"
            aria-label="Home renovation"
          >
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/80 to-zinc-900/40" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 py-16 w-full">
            <div className="flex items-center gap-2 text-yellow-400 mb-4">
              <MapPin className="h-4 w-4" />
              <span className="text-sm tracking-wider">{area.region}</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-light text-zinc-100 max-w-3xl mb-6">
              Home renovation in {place}
            </h1>
            <p className="text-zinc-300 max-w-2xl mb-8 leading-relaxed">{area.intro}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:9149606433"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-yellow-400 text-zinc-900"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm tracking-wider font-medium">(914) 960-6433</span>
              </a>
              <Link
                to="/consultation"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-zinc-900 transition-colors"
              >
                <span className="text-sm tracking-wider">Free Consultation</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-16 mb-20">
            <div>
              <h2 className="text-3xl font-light text-zinc-100 mb-6">What we renovate in {area.name}</h2>
              <p className="text-zinc-400 leading-relaxed mb-8">{area.housing}</p>
              <ul className="space-y-3">
                {area.focus.map((item) => (
                  <li key={item} className="text-zinc-300 flex items-center gap-3">
                    <span className="h-1.5 w-1.5 bg-yellow-400 rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-light text-zinc-100 mb-6">Services</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((service) => (
                  <Link
                    key={service.path}
                    to={service.path}
                    className="bg-zinc-800/30 border border-zinc-800 p-5 text-zinc-200 hover:border-yellow-400/40 transition-colors flex items-center justify-between gap-3"
                  >
                    <span>{service.name}</span>
                    <ArrowRight className="h-4 w-4 text-yellow-400" />
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <section>
            <h2 className="text-3xl font-light text-zinc-100 mb-8">Other towns we serve</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {serviceAreas
                .filter((item) => item.slug !== area.slug)
                .map((item) => (
                  <Link
                    key={item.slug}
                    to={`/areas/${item.slug}`}
                    className="bg-zinc-800/30 p-4 text-center text-zinc-300 hover:text-yellow-400 transition-colors"
                  >
                    {item.name}
                  </Link>
                ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
